import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { api } from "@/lib/api";
import { Quote } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Markdown } from "@/components/ui/markdown";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { AppLayout } from "@/components/layout/AppLayout";

export default function QuotePage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuote, setEditedQuote] = useState<Partial<Quote>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchQuote();
    }
  }, [id]);

  const fetchQuote = async () => {
    try {
      setIsLoading(true);
      const response = await api(`/quotes/${id}`);
      setQuote(response as Quote);
      setEditedQuote(response as Quote);
    } catch (error) {
      console.error("Error fetching quote:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!quote) return;

    try {
      setIsSaving(true);
      const response = await api(`/quotes/${id}`, {
        method: "PUT",
        body: editedQuote,
      });
      setQuote(response as Quote);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating quote:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!quote || !confirm("Are you sure you want to delete this quote?")) return;

    try {
      await api(`/quotes/${id}`, {
        method: "DELETE",
      });
      navigate("/quotes");
    } catch (error) {
      console.error("Error deleting quote:", error);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="container mx-auto py-6 px-6">
          <div className="text-center">Loading quote...</div>
        </div>
      </AppLayout>
    );
  }

  if (!quote) {
    return (
      <AppLayout>
        <div className="container mx-auto py-6 px-6">
          <div className="text-center">Quote not found</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="container mx-auto py-6 px-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/quotes")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Quote</h1>
      </div>

      <Card className="p-6">
        {isEditing ? (
          <div className="space-y-4">
            <Textarea
              placeholder="Quote content..."
              value={editedQuote.content}
              onChange={(e) => setEditedQuote({ ...editedQuote, content: e.target.value })}
              className="min-h-[100px]"
            />
            <Input
              placeholder="Author"
              value={editedQuote.author}
              onChange={(e) => setEditedQuote({ ...editedQuote, author: e.target.value })}
            />
            <Input
              placeholder="Source (optional)"
              value={editedQuote.source ?? ""}
              onChange={(e) => setEditedQuote({ ...editedQuote, source: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditedQuote(quote);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-lg italic whitespace-pre-wrap">
              "<Markdown content={quote.content} inline />"
            </div>
            <div className="text-sm text-muted-foreground">
              — {quote.author}
              {quote.source && `, ${quote.source}`}
            </div>
            <div className="text-sm text-muted-foreground">
              Created {formatDistanceToNow(new Date(quote.created_at), { addSuffix: true })}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
    </AppLayout>
  );
}